/**
 * IoniconsService - Optimized icon loading service
 * 
 * BUNDLE OPTIMIZATION: This service imports only the ~40 icons actually used
 * in the application instead of the entire ionicons library (~1400 icons, 770KB).
 * 
 * To add a new icon:
 * 1. Import it from 'ionicons/icons' (camelCase name)
 * 2. Add it to the AVAILABLE_ICONS map (kebab-case -> import)
 * 3. Use it in templates with <ion-icon name="kebab-case-name">
 */
import { Injectable } from '@angular/core';
import { addIcons } from 'ionicons';

// Import ONLY the icons actually used in the application
// This reduces bundle size from ~770KB to ~15KB for icons
import {
    add,
    addCircleOutline,
    alertCircleOutline,
    arrowForward,
    call,
    card,
    cash,
    checkboxOutline,
    checkmarkCircle,
    checkmarkCircleOutline,
    chevronForward,
    close,
    closeCircle,
    colorWand,
    construct,
    documentOutline,
    documents,
    documentsOutline,
    ellipsisHorizontal,
    ellipsisVertical,
    eye,
    eyeSharp,
    gitBranch,
    globe,
    helpCircle,
    home,
    informationCircle,
    locate,
    mailOpenOutline,
    map,
    people,
    pricetags,
    refresh,
    refreshOutline,
    removeCircleOutline,
    removeOutline,
    search,
    settingsOutline,
    shieldCheckmarkOutline,
    statsChart,
    trash
} from 'ionicons/icons';

// Map of kebab-case icon names to their imported values
// When adding a new icon, add it here with: 'kebab-name': importedName
const AVAILABLE_ICONS: Record<string, string> = {
    'add': add,
    'add-circle-outline': addCircleOutline,
    'alert-circle-outline': alertCircleOutline,
    'arrow-forward': arrowForward,
    'call': call,
    'card': card,
    'cash': cash,
    'checkbox-outline': checkboxOutline,
    'checkmark-circle': checkmarkCircle,
    'checkmark-circle-outline': checkmarkCircleOutline,
    'chevron-forward': chevronForward,
    'close': close,
    'close-circle': closeCircle,
    'color-wand': colorWand,
    'construct': construct,
    'document-outline': documentOutline,
    'documents': documents,
    'documents-outline': documentsOutline,
    'ellipsis-horizontal': ellipsisHorizontal,
    'ellipsis-vertical': ellipsisVertical,
    'eye': eye,
    'eye-sharp': eyeSharp,
    'git-branch': gitBranch,
    'globe': globe,
    'help-circle': helpCircle,
    'home': home,
    'information-circle': informationCircle,
    'locate': locate,
    'mail-open-outline': mailOpenOutline,
    'map': map,
    'people': people,
    'pricetags': pricetags,
    'refresh': refresh,
    'refresh-outline': refreshOutline,
    'remove-circle-outline': removeCircleOutline,
    'remove-outline': removeOutline,
    'search': search,
    'settings-outline': settingsOutline,
    'shield-checkmark-outline': shieldCheckmarkOutline,
    'stats-chart': statsChart,
    'trash': trash
};

@Injectable({
    providedIn: 'root'
})
export class IoniconsService {
    constructor() {
        // Register all icons at startup - they're already imported so no additional cost
        addIcons(AVAILABLE_ICONS);
    }

    /**
     * Gets a list of all available icon names
     * @returns Array of available icon names (kebab-case)
     */
    public getAvailableIcons(): string[] {
        return Object.keys(AVAILABLE_ICONS);
    }

    /**
     * Checks if an icon is available
     * @param iconName - Name of the icon to check (kebab-case)
     * @returns True if icon is available
     */
    public isIconAvailable(iconName: string): boolean {
        return iconName in AVAILABLE_ICONS;
    }
}
